import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Observable, of, Subscription } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { AuthenticationService } from 'src/app/core/services/authentication.service';
import { CompanyService } from 'src/app/setting/services/company.service';
import { EventService } from '../../../../event/state/event.service';
import { SummernoteHelper } from '../../../../shared/common/SummernoteHelper';
import { CustomField } from '../../../../shared/models/CustomField';
import { ValueLabel } from '../../../../shared/models/ValueLabel';
import { AppearanceSettingCards, AvailableWidgets, hiddenCategories, hiddenEntities, hiddenWidgets, WidgetConfigRow } from '../../../models/AvailableWidgets';
import { EmployeeDatetimeType } from '../../../models/EmployeeDatetimeType';
import { Role } from '../../../models/Role';
import { DatetimesTypesStoreService } from '../../../services/datetimes-types-store.service';
import { RolesService } from '../../../services/roles.service';

@Component({
    selector: 'app-setting-appearance',
    templateUrl: './setting-appearance.component.html',
    styleUrls: ['./setting-appearance.component.css']
})
export class SettingAppearanceComponent implements OnInit, OnDestroy {
    public canViewCompanySettings$: Observable<boolean>;
    public canEditCompanySettings$: Observable<boolean>;
    public availableWidgets$: Observable<AvailableWidgets>;
    public datetimeTypes$: Observable<EmployeeDatetimeType[]>;
    public settings$: Observable<any>;
    public eventTypes$: Observable<ValueLabel[]>;
    public roles$: Observable<Role[]>;
    public eventTypesEmployee$: Observable<ValueLabel[]>;
    public questionnaireForm: UntypedFormGroup;
    public employeeTableDisplayModeForm: UntypedFormGroup;
    public eventsTableDisplayModeForm: UntypedFormGroup;
    public attendanceStatsForm: UntypedFormGroup;
    public businessPartnersForm: UntypedFormGroup;
    public customFieldDefinitions: CustomField[] = [];
    public collapsedSettingSections: AppearanceSettingCards = {
        dashboard: true,
        employee: true,
        employee_contact_list: true,
        employee_creation: true,
        employee_table: true,
        employee_table_archived: true,
        employee_table_contractors: true,
        events_table: true,
        property_table: true,
        salary_table: true,
        attendance_stats: true,
        employee_table_display_mode: true,
        events_table_display_mode: true,
        questionnaire: true,
        business_partner: true
    };
    public dashboardWidgetsForm: AvailableWidgets['dashboard']['widgets_config'];
    public readonly hiddenEntities = hiddenEntities;
    public readonly hiddenCategories = hiddenCategories;
    public readonly hiddenWidgets = hiddenWidgets;

    public displayModeOptionsEmployeeTable = [
        'WHOLE_COMPANY',
        'SIBLINGS',
        'DESCENDANTS_AND_SIBLINGS',
        'DESCENDANTS',
        'SELF_ONLY',
        'CHILDREN',
    ];

    public displayModeOptionsEventsTable = [
        'DESCENDANTS_AND_SIBLINGS',
        'DESCENDANTS',
        'TEAM',
        'WHOLE_COMPANY',
    ];

    public attendanceStatsPlacements = [
        'EMPLOYEE',
        'ATTENDANCE',
    ];

    private _availableWidgetsSubscription: Subscription;

    public constructor(
        public summernoteHelper: SummernoteHelper,
        private _authService: AuthenticationService,
        private _companyService: CompanyService,
        private _datetimeTypesStore: DatetimesTypesStoreService,
        private _eventService: EventService,
        private _roleService: RolesService,
        private _fb: UntypedFormBuilder,
    ) { }

    public ngOnInit(): void {
        for (const section of Object.keys(this.collapsedSettingSections)) {
            if (window.location.hash.includes(section)) {
                this.collapsedSettingSections[section] = false;
                break;
            }
        }

        this.datetimeTypes$ = this._datetimeTypesStore.datetimeTypes$;

        this.canEditCompanySettings$ = this._authService.hasPermissionTo('companySetting.edit')
            .pipe(map(permission => permission.can));

        this.canViewCompanySettings$ = this._authService.hasPermissionTo('companySetting.view')
            .pipe(map(permission => permission.can));

        this.questionnaireForm = this._fb.group({
            'appearance.questionnaire.identify_company_by': ['COMPANY'],
            'appearance.questionnaire.introduction_text': [''],
            'appearance.questionnaire.show_introduction_text': [''],
            'appearance.questionnaire.ending_text': [''],
            'appearance.questionnaire.show_ending_text': ['']
        });

        this.employeeTableDisplayModeForm = this._fb.group({
            'appearance.widgets.employee_table.settings.display_mode': ['WHOLE_COMPANY'],
        });

        this.eventsTableDisplayModeForm = this._fb.group({
            'company.events.display_mode': ['WHOLE_COMPANY'],
            'company.events.full_types': [[]],
            'company.events.simple_types': [[]],
            'company.events.full_roles': [[]],
            'company.events.simple_roles': [[]],
        });

        this.businessPartnersForm = this._fb.group({
            'appearance.company_groups.business_partner': [''],
        });

        this.attendanceStatsForm = this._fb.group({
            'appearance.widgets.attendance_stats.placement': [],
            'appearance.widgets.attendance_stats.employee_datetime_type_IDs': [],
            'appearance.widgets.attendance_stats.show_weekly_stats': [''],
            'appearance.widgets.attendance_stats.show_monthly_stats': [''],
            'appearance.widgets.attendance_stats.show_days_in_company': [''],
        });

        this.availableWidgets$ = this._companyService.availableWidgets$
            .pipe(tap(availableWidgets => {
                this.employeeTableDisplayModeForm.patchValue({
                    'appearance.widgets.employee_table.settings.display_mode': availableWidgets.employee_table.settings.display_mode ?? 'WHOLE_COMPANY',
                });

                this.attendanceStatsForm.patchValue({
                    'appearance.widgets.attendance_stats.placement': availableWidgets.attendance_stats.placement ?? [],
                    'appearance.widgets.attendance_stats.employee_datetime_type_IDs': availableWidgets.attendance_stats.employee_datetime_type_IDs ?? [],
                    'appearance.widgets.attendance_stats.show_weekly_stats': availableWidgets.attendance_stats.show_weekly_stats,
                    'appearance.widgets.attendance_stats.show_monthly_stats': availableWidgets.attendance_stats.show_monthly_stats,
                    'appearance.widgets.attendance_stats.show_days_in_company': availableWidgets.attendance_stats.show_days_in_company,
                });

                this.dashboardWidgetsForm = availableWidgets.dashboard.widgets_config;
            }));

        this.settings$ = this._companyService.getCompanySetting()
            .pipe(
                tap(settings => {
                    this.questionnaireForm.patchValue({
                        'appearance.questionnaire.identify_company_by': settings.appearance?.questionnaire?.identify_company_by ?? 'COMPANY',
                        'appearance.questionnaire.introduction_text': settings.appearance?.questionnaire?.introduction_text,
                        'appearance.questionnaire.show_introduction_text': settings.appearance?.questionnaire?.show_introduction_text === '1',
                        'appearance.questionnaire.ending_text': settings.appearance?.questionnaire?.ending_text,
                        'appearance.questionnaire.show_ending_text': settings.appearance?.questionnaire?.show_ending_text === '1'
                    });

                    this.eventsTableDisplayModeForm.patchValue({
                        'company.events.display_mode': settings.company_settings.events.display_mode,
                        'company.events.full_types': settings.company_settings.events.full_types,
                        'company.events.full_roles': settings.company_settings.events.full_roles,
                        'company.events.simple_types': settings.company_settings.events.simple_types,
                        'company.events.simple_roles': settings.company_settings.events.simple_roles,
                    });

                    this.businessPartnersForm.patchValue({
                        'appearance.company_groups.business_partner': settings.appearance?.company_groups?.business_partner,
                    });
                }),
                shareReplay()
            );

        this.eventTypes$ = this._eventService.getEventTypes().pipe(
            tap(events => {
                this.eventTypesEmployee$ = of(events.filter(event => event.value === 'birthday' || event.value === 'employee_anniversary'));
            })
        );

        this.roles$ = this._roleService.getAllRoles().pipe(shareReplay());
    }

    public ngOnDestroy(): void {
        this._availableWidgetsSubscription?.unsubscribe();
    }

    public setCustomFieldDefinitions(customFieldDefinitions: CustomField[]): void {
        this.customFieldDefinitions = customFieldDefinitions;
    }

    public changeWidgets(type: string, category: string, subKey: string, event: any): void {
        const body = {
            key: `${type}.${category}.${subKey}`,
            value: event.target.checked
        };

        this._companyService.updateAvailableWidget(body)
            .then((result: { message: string, available_widgets: AvailableWidgets }) => {
                this._companyService.updateAvailableWidgetsValue(result.available_widgets);
            });
    }

    public onSubmitQustionnaire(): void {
        const value = this.questionnaireForm.value;

        value.settings_type = 'questionnaire';

        void this._companyService.saveCompanySetting(this.questionnaireForm.value);
    }

    public onSubmitAttendanceStats(): void {
        const value = this.attendanceStatsForm.value;

        value.settings_type = 'attendance_stats';

        void this._companyService.saveCompanySetting(this.attendanceStatsForm.value);
    }

    public onSubmitBusinessPartners(): void {
        const value = this.businessPartnersForm.value;

        value.settings_type = 'company_group';

        void this._companyService.saveCompanySetting(this.businessPartnersForm.value);
    }

    public onSubmitEmployeeTableDisplayMode(): void {
        const body = {
            key: `employee_table.settings.display_mode`,
            value: this.employeeTableDisplayModeForm.value['appearance.widgets.employee_table.settings.display_mode']
        };

        this._companyService.updateAvailableWidget(body)
            .then((result: { message: string, available_widgets: AvailableWidgets }) => {
                this._companyService.updateAvailableWidgetsValue(result.available_widgets);
            });
    }

    public onSubmitEventsTableDisplayMode(): void {
        const value = this.eventsTableDisplayModeForm.value;

        value.settings_type = 'event';

        void this._companyService.saveCompanySetting(this.eventsTableDisplayModeForm.value);
    }

    public getEntities(availableWidgets: AvailableWidgets): string[] {
        return Object.keys(availableWidgets);
    }

    public onSubmitDashboardOrder(): void {
        this._companyService.updateAvailableWidgetsOrder(this.dashboardWidgetsForm)
            .then((result: { message: string, available_widgets: AvailableWidgets }) => {
                this._companyService.updateAvailableWidgetsValue(result.available_widgets);
            });
    }

    public moveUp(widget: WidgetConfigRow): void {
        const previousWidget = this.dashboardWidgetsForm.find(w => w.order === widget.order - 1);
        if (previousWidget.disabled) {
            return;
        }

        const newWidgetsForm = [
            ...this.dashboardWidgetsForm,
        ];

        newWidgetsForm[previousWidget.order] = {...widget, order: widget.order - 1};
        newWidgetsForm[widget.order] = {...previousWidget, order: widget.order};

        this.dashboardWidgetsForm = [...newWidgetsForm];

        this.onSubmitDashboardOrder();
    }

    public moveDown(widget: WidgetConfigRow): void {
        const nextWidget = this.dashboardWidgetsForm.find(w => w.order === widget.order + 1);
        if (!nextWidget) {
            return;
        }

        const newWidgetsForm = [
            ...this.dashboardWidgetsForm,
        ];

        newWidgetsForm[nextWidget.order] = {...widget, order: widget.order + 1};
        newWidgetsForm[widget.order] = {...nextWidget, order: widget.order};
        this.dashboardWidgetsForm = [...newWidgetsForm];

        this.onSubmitDashboardOrder();
    }

    public onShowChange($event: any, widget: WidgetConfigRow): void {
        const index = this.dashboardWidgetsForm.indexOf(widget);
        const newWidgetsForm = [
            ...this.dashboardWidgetsForm,
        ];

        newWidgetsForm[index] = {...widget, show: !widget.show};
        this.dashboardWidgetsForm = [...newWidgetsForm];

        this.onSubmitDashboardOrder();
    }
}
